import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import copy from 'clipboard-copy';
import { template } from 'lodash';
import { ssColorBlack, ssColorCyan, ssColorWhite } from '../../constants/style-constants';
import { vunit } from '../../lib/style-helpers';
import { DisplayHeader } from '../Typography';
import phrasesType from '../../proptypes/phrases';
import SocialSharing from '../SocialSharing';

const contentCSS = css`
  background: ${ssColorBlack};
  padding: ${vunit(6)};

  &, h1, h2, h3, h4 {
    color: ${ssColorWhite};
  }

  ${DisplayHeader} {
    color: ${ssColorCyan};
  }
`;

const urlBoxCSS = css`
  background: ${ssColorWhite};
  color: ${ssColorBlack};
  border: 1px solid ${ssColorCyan};
  padding: ${vunit(2)};
  margin-bottom: ${vunit(3)};
  text-align: center;
  font-weight: bold;
`;

const buttonCSS = css`
  margin-bottom: ${vunit(2)};
`;

const InviteForm = ({
  phrases,
  referralUrl,
  onCopy,
  onShare,
}) => {
  const {
    referralInviteTitle,
    referralInviteBody,
    copyLinkButton,
    share: {
      emailBody,
      emailSubject,
      twitter,
      whatsApp,
    },
  } = phrases;

  const [buttonClicked, setButtonClicked] = useState(false);
  const inputRef = useRef();

  const copyButtonPhrase = buttonClicked
    ? copyLinkButton.completedState
    : copyLinkButton.instructionState;

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleClick = async () => {
    inputRef.current.select();
    await copy(referralUrl);
    setButtonClicked(true);
    setTimeout(() => {
      setButtonClicked(false);
      inputRef.current.setSelectionRange(0, 0);
    }, 1500);
    onCopy();
  };

  const twitterTemplate = template(twitter);
  const whatsAppTemplate = template(whatsApp);
  const emailBodyTemplate = template(emailBody);

  const networks = [
    {
      id: 'Facebook',
      url: `https://www.facebook.com/sharer.php?u=${encodeURIComponent(referralUrl)}`,
      title: 'Share on Facebook',
    },
    {
      id: 'Twitter',
      url: `https://twitter.com/intent/tweet?text=${encodeURI(twitterTemplate({ referralUrl }))}`,
      title: 'Share on Twitter',
    },
    {
      id: 'WhatsApp',
      url: `https://api.whatsapp.com/send?text=${encodeURI(whatsAppTemplate({ referralUrl }))}`,
      title: 'Share on WhatsApp',
    },
    {
      id: 'Email',
      url: `mailto:?subject=${encodeURI(emailSubject)}&body=${encodeURI(emailBodyTemplate({ referralUrl }))}`,
      title: 'Share via email',
    },
  ];

  return (
    <div css={contentCSS}>
      <h2 className="headline">{referralInviteTitle}</h2>
      <p>
        {referralInviteBody}
      </p>
      <input
        css={urlBoxCSS}
        onFocus={handleFocus}
        ref={inputRef}
        value={referralUrl}
        className="form-control"
        readOnly
      />
      <button
        className="btn btn-primary"
        type="button"
        disabled={buttonClicked}
        onClick={handleClick}
        css={buttonCSS}
      >
        {copyButtonPhrase}
      </button>
      <SocialSharing
        networks={networks}
        namespace="invite-friends"
        onShare={onShare}
      />
    </div>
  );
};

InviteForm.defaultProps = {
  onCopy: () => undefined,
  onShare: () => undefined,
};

InviteForm.propTypes = {
  referralUrl: PropTypes.string.isRequired,
  phrases: phrasesType.isRequired,
  onCopy: PropTypes.func,
  onShare: PropTypes.func,
};

export default InviteForm;
